<template>
  <ComponentHeader title="Relationship Manager" subtitle="Manage parent-child relationships between entities" />

  <div class="entity-relationship-manager p-4 space-y-4">
    <!-- Parent Entity Selection -->
    <div class="bg-white rounded-lg border border-gray-200 p-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Select Parent Entity</h3>

      <div class="flex items-center gap-4">
        <EntitySearchSingle
          button-text="Select Parent Entity"
          modal-title="Select Parent Entity"
          @entity-selected="onParentSelected"
        />

        <div v-if="selectedParent" class="flex items-center gap-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg flex-1">
          <div class="flex-1">
            <div class="font-medium text-gray-900">{{ selectedParent.name }}</div>
            <div class="text-sm text-gray-500">{{ selectedParent.entity_type_name }}</div>
          </div>
          <button
            @click="clearParent"
            class="text-gray-400 hover:text-gray-600 transition-colors"
            title="Clear selection"
          >
            <FontAwesomeIcon :icon="['fas', 'times']" />
          </button>
        </div>
      </div>
    </div>

    <!-- Children List -->
    <div v-if="selectedParent" class="bg-white rounded-lg border border-gray-200 p-6">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold text-gray-900">
          Children of: {{ selectedParent.name }}
        </h3>
        <EntitySearchSingle
          button-text="Add Children"
          modal-title="Select Child Entities"
          :multiple="true"
          @entities-selected="onChildrenSelected"
        />
      </div>

      <!-- Loading State -->
      <LoadingIndicator v-if="loadingChildren" message="Loading relationships..." />

      <!-- Empty State -->
      <div v-else-if="children.length === 0" class="text-center py-12">
        <FontAwesomeIcon :icon="['fas', 'inbox']" class="text-4xl text-gray-300 mb-3" />
        <p class="text-gray-500">No children assigned to this entity</p>
        <p class="text-sm text-gray-400 mt-1">Click "Add Children" to assign child entities</p>
      </div>

      <!-- Children Table -->
      <div v-else class="space-y-2">
        <div class="flex justify-between items-center mb-3">
          <div class="text-sm text-gray-600">
            {{ children.length }} {{ children.length === 1 ? 'child' : 'children' }}
          </div>
          <button
            v-if="selectedChildrenForRemoval.length > 0"
            @click="bulkRemoveChildren"
            class="btn-danger-sm"
          >
            <FontAwesomeIcon :icon="['fas', 'trash']" />
            Remove Selected ({{ selectedChildrenForRemoval.length }})
          </button>
        </div>

        <div class="border border-gray-200 rounded-lg overflow-hidden">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    :checked="selectedChildrenForRemoval.length === children.length && children.length > 0"
                    @change="toggleSelectAll"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                  />
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="child in children"
                :key="child.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    :checked="selectedChildrenForRemoval.includes(child.id)"
                    @change="toggleChildSelection(child.id)"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark"
                  />
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ child.name }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-500">{{ child.entity_type_name }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <button
                    @click="removeChild(child)"
                    class="text-red-600 hover:text-red-800 transition-colors"
                    title="Remove child"
                  >
                    <FontAwesomeIcon :icon="['fas', 'times']" class="text-lg" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div
      v-if="errorMessage"
      class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
    >
      <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" class="text-red-600 mt-0.5" />
      <div class="flex-1">
        <div class="font-medium text-red-800">Error</div>
        <div class="text-sm text-red-700 mt-1">{{ errorMessage }}</div>
      </div>
      <button
        @click="errorMessage = ''"
        class="text-red-400 hover:text-red-600 transition-colors"
      >
        <FontAwesomeIcon :icon="['fas', 'times']" />
      </button>
    </div>

    <!-- Success Display -->
    <div
      v-if="successMessage"
      class="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
    >
      <FontAwesomeIcon :icon="['fas', 'check-circle']" class="text-green-600 mt-0.5" />
      <div class="flex-1">
        <div class="font-medium text-green-800">Success</div>
        <div class="text-sm text-green-700 mt-1">{{ successMessage }}</div>
      </div>
      <button
        @click="successMessage = ''"
        class="text-green-400 hover:text-green-600 transition-colors"
      >
        <FontAwesomeIcon :icon="['fas', 'times']" />
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntitySearchSingle from '../widgetcomponents/Search_Widgets/EntitySearch_Single.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'EntityRelationshipManager',

  components: {
    FontAwesomeIcon,
    EntitySearchSingle,
    ComponentHeader,
    LoadingIndicator
  },

  data() {
    return {
      selectedParent: null,
      children: [],
      loadingChildren: false,
      errorMessage: '',
      successMessage: '',
      selectedChildrenForRemoval: []
    };
  },

  methods: {
    onParentSelected(entity) {
      this.selectedParent = entity;
      this.loadChildren();
    },

    clearParent() {
      this.selectedParent = null;
      this.children = [];
      this.selectedChildrenForRemoval = [];
      this.errorMessage = '';
      this.successMessage = '';
    },

    async loadChildren() {
      if (!this.selectedParent) return;

      this.loadingChildren = true;
      this.errorMessage = '';
      this.selectedChildrenForRemoval = [];

      try {
        // Get all entities that have this entity as a parent
        const response = await axios.get(`/api/entities/`, {
          params: {
            parent_id: this.selectedParent.id,
            include_descendants: false  // Direct children only
          }
        });

        this.children = response.data.results || response.data;
      } catch (error) {
        console.error('Failed to load children:', error);
        this.errorMessage = error.response?.data?.detail || 'Failed to load children';
        this.children = [];
      } finally {
        this.loadingChildren = false;
      }
    },

    async onChildrenSelected(entities) {
      if (!this.selectedParent) {
        this.errorMessage = 'Please select a parent entity first';
        return;
      }

      if (!entities || entities.length === 0) {
        return;
      }

      try {
        // Call the bulk-add-children endpoint
        const response = await axios.post(`/api/entities/${this.selectedParent.id}/bulk-add-children/`, {
          child_ids: entities.map(e => e.id)
        });

        const { summary, results } = response.data;

        // Build success message
        const messages = [];
        if (summary.added > 0) {
          messages.push(`Successfully added ${summary.added} ${summary.added === 1 ? 'child' : 'children'}`);
        }
        if (summary.skipped > 0) {
          messages.push(`${summary.skipped} already existed`);
        }
        if (summary.errors > 0) {
          messages.push(`${summary.errors} failed`);
        }

        if (summary.added > 0 || summary.skipped > 0) {
          this.successMessage = messages.join(', ');
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        }

        // Show errors if any
        if (results.errors && results.errors.length > 0) {
          const errorDetails = results.errors.map(e => `${e.id}: ${e.reason}`).join(', ');
          this.errorMessage = `Some entities failed: ${errorDetails}`;
        }

        // Reload children list
        await this.loadChildren();

      } catch (error) {
        console.error('Failed to add children:', error);
        this.errorMessage = error.response?.data?.error || 'Failed to add child relationships';
      }
    },

    async removeChild(child) {
      if (!confirm(`Remove ${child.name} from ${this.selectedParent.name}?`)) {
        return;
      }

      try {
        // Call the remove-parent endpoint
        await axios.post(`/api/entities/${child.id}/remove-parent/`, {
          parent_id: this.selectedParent.id
        });

        this.successMessage = `Successfully removed ${child.name} from ${this.selectedParent.name}`;

        // Reload children list
        await this.loadChildren();

        // Clear success message after 3 seconds
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);

      } catch (error) {
        console.error('Failed to remove child:', error);
        this.errorMessage = error.response?.data?.detail || error.response?.data?.error || 'Failed to remove child relationship';
      }
    },

    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedChildrenForRemoval = this.children.map(child => child.id);
      } else {
        this.selectedChildrenForRemoval = [];
      }
    },

    toggleChildSelection(childId) {
      const index = this.selectedChildrenForRemoval.indexOf(childId);
      if (index === -1) {
        this.selectedChildrenForRemoval.push(childId);
      } else {
        this.selectedChildrenForRemoval.splice(index, 1);
      }
    },

    async bulkRemoveChildren() {
      if (this.selectedChildrenForRemoval.length === 0) {
        return;
      }

      const count = this.selectedChildrenForRemoval.length;
      if (!confirm(`Remove ${count} ${count === 1 ? 'child' : 'children'} from ${this.selectedParent.name}?`)) {
        return;
      }

      try {
        // Call the bulk-remove-children endpoint
        const response = await axios.post(`/api/entities/${this.selectedParent.id}/bulk-remove-children/`, {
          child_ids: this.selectedChildrenForRemoval
        });

        const { summary, results } = response.data;

        // Build success message
        const messages = [];
        if (summary.removed > 0) {
          messages.push(`Successfully removed ${summary.removed} ${summary.removed === 1 ? 'child' : 'children'}`);
        }
        if (summary.errors > 0) {
          messages.push(`${summary.errors} failed`);
        }

        if (summary.removed > 0) {
          this.successMessage = messages.join(', ');
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        }

        // Show errors if any
        if (results.errors && results.errors.length > 0) {
          const errorDetails = results.errors.map(e => `${e.id}: ${e.reason}`).join(', ');
          this.errorMessage = `Some entities failed: ${errorDetails}`;
        }

        // Clear selection
        this.selectedChildrenForRemoval = [];

        // Reload children list
        await this.loadChildren();

      } catch (error) {
        console.error('Failed to remove children:', error);
        this.errorMessage = error.response?.data?.error || 'Failed to remove child relationships';
      }
    }
  }
};
</script>

<style scoped>
/* Component-specific styles if needed */
</style>
